import { useMemo, useState } from 'react'

import {
  IntegrationsJoinUpdateCompanyIntegrationDocument,
  JoinExternalIntegrationFragment,
} from 'generated/resources/graphql'
import { uniq, without } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export interface ConfigurationValues {
  [key: string]: string | undefined | null
}

interface Props {
  integrationProfile: JoinExternalIntegrationFragment
}

export const useUpdateIntegrationProfile = ({ integrationProfile }: Props) => {
  const { access, company } = useCurrentSession()
  const [fieldsErrors, setFieldsErrors] = useState<string[]>([])
  const [integrationFile, setIntegrationFile] = useState<File | undefined>()
  const [configurationValues, setConfigurationValues] = useState<ConfigurationValues>(
    Object.fromEntries(
      integrationProfile.configuration.map((config) => [config.slug, config.value])
    )
  )

  const [updateCompanyIntegration, { loading: updatingCompanyIntegration }] = useMutation(
    IntegrationsJoinUpdateCompanyIntegrationDocument,
    {
      extractorFn: (response) =>
        response.integrationsJoinMutations.updateIntegrationsjoinIntegration,
    }
  )

  const generateConfiguration = () =>
    Object.keys(configurationValues).map((key) => ({
      slug: key,
      value: configurationValues[key],
    }))

  const validateForm = () => {
    const missingFields = integrationProfile.configuration
      .filter((config) => {
        if (config.validationPattern === 'FILE') {
          return !integrationFile
        }
        return !configurationValues[config.slug]
      })
      .map((config) => config.slug)

    if (missingFields.length > 0) {
      setFieldsErrors(missingFields)
      return false
    }

    return true
  }

  const updateIntegrationProfile = async (deactivate = false) => {
    if (updatingCompanyIntegration || !validateForm()) return

    const result = await updateCompanyIntegration({
      variables: {
        accessId: access.id,
        companyId: company.id,
        status: deactivate ? 'disabled' : 'enabled',
        file: integrationFile,
        integrationSlug: integrationProfile.slug,
        configuration: generateConfiguration(),
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return handled
  }

  const configurationPatters = useMemo(
    () =>
      Object.fromEntries(
        integrationProfile.configuration.map((config) => [config.slug, config.validationPattern])
      ),
    [integrationProfile]
  )

  const hasValidInput = (slug: string, value: string) => {
    const pattern = configurationPatters[slug]

    if (!value || !pattern) return true

    return new RegExp(pattern).test(value)
  }

  const onConfigurationValueChange = (slug: string, value: string) => {
    setConfigurationValues({ ...configurationValues, ...{ [slug]: value } })

    const isValid = value && hasValidInput(slug, value)

    setFieldsErrors(isValid || !value ? without(fieldsErrors, slug) : uniq([...fieldsErrors, slug]))
  }

  const onConfigurationFileChange = (slug: string, file: File | undefined) => {
    setIntegrationFile(file)

    setConfigurationValues({ ...configurationValues, ...{ [slug]: file?.name || 'FILE' } })
  }
  return {
    fieldsErrors,
    integrationFile,
    configurationValues,
    updateIntegrationProfile,
    updatingCompanyIntegration,
    onConfigurationValueChange,
    onConfigurationFileChange,
  }
}
